import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Instagram } from "lucide-react";
import { FaTiktok, FaWhatsapp } from "react-icons/fa";
import { TechArticleCard } from "@/components/TechArticleCard";
import { BottomNavigation } from "@/components/BottomNavigation";
import { ArticleDetail } from "@/components/ArticleDetail";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import { useArticles, Article } from "@/hooks/useArticles";
import MiniMapbox from "@/components/MiniMapbox";

const Index = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const navigate = useNavigate();

  const { articles, userLikes, loading, recordView, toggleLike } = useArticles(user);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate("/auth");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    recordView(article.id);
  };

  const handleLike = (articleId: string) => {
    toggleLike(articleId);
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
            Tecly
          </h1>
          <div className="flex items-center gap-3">
            <a href="https://www.instagram.com/tecly.it?igsh=MXR1bmQ0bGdtbjBsNw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 rounded-full transition-all duration-300 group">
              <Instagram className="w-5 h-5 text-muted-foreground group-hover:text-white transition-colors duration-300" />
            </a>
            <a href="https://www.tiktok.com/@tecly6?_r=1&_t=ZN-92lek6T8jry" target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gradient-to-br hover:from-[#69C9D0] hover:to-[#EE1D52] rounded-full transition-all duration-300 group" style={{ backgroundColor: 'transparent' }}>
              <FaTiktok className="w-5 h-5 text-muted-foreground group-hover:text-white transition-colors duration-300" />
            </a>
            <a href="https://whatsapp.com/channel/0029VbCTNmGICVft1h5A2z2u" target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-[#25D366] rounded-full transition-all duration-300 group">
              <FaWhatsapp className="w-5 h-5 text-muted-foreground group-hover:text-white transition-colors duration-300" />
            </a>
          </div>
        </div>
      </header>

      {/* Articles Grid */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {articles.map((article) => (
            <TechArticleCard 
              key={article.id} 
              image={article.image}
              title={article.title}
              description={article.description}
              views={article.views_count}
              liked={userLikes.has(article.id)}
              likes={article.likes_count}
              onLike={() => handleLike(article.id)}
              onClick={() => handleArticleClick(article)}
            />
          ))}
        </div>

        {/* Apple Stores Map */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-6 text-foreground">Apple Store nelle vicinanze</h2>
          <MiniMapbox />
        </section>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} activeTab="home" />

      {/* Menu Drawer */}
      <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />

      {/* Article Detail View */}
      {selectedArticle && (
        <ArticleDetail 
          image={selectedArticle.image}
          title={selectedArticle.title}
          description={selectedArticle.description}
          views={selectedArticle.views_count}
          liked={userLikes.has(selectedArticle.id)}
          content={selectedArticle.content as { type: "text" | "image"; content: string }[] | undefined}
          onLike={() => handleLike(selectedArticle.id)}
          onClose={() => setSelectedArticle(null)}
        />
      )}
      </div>
    </PageTransition>
  );
};

export default Index;
